import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Iconify from 'src/components/Iconify';
import { TAREFAS_DESIGN } from './Tarefas.design';
import TarefaCard, { columnsFromWorkflow } from './TarefaCard';

const D = TAREFAS_DESIGN;

export default function KanbanBoard({
    tasks,
    onStatusChange,
    onEdit,
    onUpdate,
    workflow,
    usuariosList = [],
}) {
    const navigate = useNavigate();
    const dragItem = useRef(null);
    const [arrastando, setArrastando] = useState(null);
    const [colunaAlvo, setColunaAlvo] = useState(null);

    const columns = useMemo(() => columnsFromWorkflow(workflow), [workflow]);
    const lastColumnId = columns[columns.length - 1]?.id;

    const handleDragStart = (e, task) => {
        if (e.target.closest('[data-no-drag]')) {
            e.preventDefault();
            return;
        }
        dragItem.current = task;
        setArrastando(task._id);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragEnd = () => {
        dragItem.current = null;
        setArrastando(null);
        setColunaAlvo(null);
    };

    const handleDragOver = (e, columnId) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (colunaAlvo !== columnId) setColunaAlvo(columnId);
    };

    const handleDrop = (e, columnId) => {
        e.preventDefault();
        const task = dragItem.current;
        handleDragEnd();
        if (task && task.status !== columnId) {
            onStatusChange(task._id, columnId);
        }
    };

    if (!workflow?.estagios?.length) {
        return (
            <div className="flex flex-col items-center justify-center p-12 text-center bg-white dark:bg-[#222529] rounded-2xl border border-gray-100 dark:border-[#383838]">
                <div className="w-16 h-16 rounded-2xl bg-violet-50 dark:bg-violet-500/10 flex items-center justify-center mb-4 text-[#7C3AED]">
                    <Iconify icon="mdi:view-column-outline" width={28} />
                </div>
                <p className="text-lg font-bold text-gray-900 dark:text-[#D1D2D3] mb-1.5">Nenhum estágio configurado</p>
                <p className="text-sm text-gray-500 dark:text-[#ABABAD] max-w-sm mb-4">
                    Crie um workflow com estágios para organizar as tarefas em um quadro kanban.
                </p>
                <button
                    type="button"
                    onClick={() => navigate('/dashboard/configuracoes/tarefas-config')}
                    className="text-sm font-medium text-[#7C3AED] hover:underline"
                >
                    Configurar workflows
                </button>
            </div>
        );
    }

    return (
        <div className={D.kanban.board}>
            {columns.map((col) => {
                const colTasks = tasks.filter((t) => t.status === col.id);
                const cor = col.cor || '#7C3AED';

                return (
                    <div
                        key={col.id}
                        onDragOver={(e) => handleDragOver(e, col.id)}
                        onDrop={(e) => handleDrop(e, col.id)}
                        className={D.kanban.column}
                    >
                        <div className={D.kanban.columnHeader}>
                            <div className={D.kanban.columnIconBox} style={{ backgroundColor: `${cor}20` }}>
                                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: cor }} />
                            </div>
                            <p className={D.kanban.columnTitle}>{col.label}</p>
                            <span className={D.kanban.columnCount}>{colTasks.length}</span>
                        </div>

                        <div className={`${D.kanban.columnBody} ${colunaAlvo === col.id ? D.kanban.columnBodyDragging : ''}`}>
                            {colTasks.map((task) => (
                                <TarefaCard
                                    key={task._id}
                                    task={task}
                                    columns={columns}
                                    lastColumnId={lastColumnId}
                                    usuariosList={usuariosList}
                                    draggable
                                    dragging={arrastando === task._id}
                                    onDragStart={handleDragStart}
                                    onDragEnd={handleDragEnd}
                                    onEdit={onEdit}
                                    onStatusChange={onStatusChange}
                                    onUpdate={onUpdate}
                                />
                            ))}

                            {colTasks.length === 0 && colunaAlvo !== col.id && (
                                <div className={D.kanban.columnEmpty}>
                                    <Iconify icon="eva:inbox-outline" width={24} className="text-gray-300 mb-1.5" />
                                    <p className={D.kanban.columnEmptyText}>Arraste tarefas para cá</p>
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
